import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { ChangeEvent, MouseEvent, useContext, useState } from 'react';

import { Carousel } from '@mantine/carousel';
import { ActionIcon, Badge, Box, Flex, LoadingOverlay, Text } from '@mantine/core';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { relationApi } from '@/shared/api/relation';
import { useIcon } from '@/shared/hooks/useIcon';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import useBookingStore from '@/shared/store/booking.store.ts';
import { useSettingsStore } from '@/shared/store/settings.store.ts';
import { IPlaceType, IRequestError, TDate } from '@/shared/types/app.types';
import {
	BookingObjectKinds,
	BookingType,
	IAvaliableObject,
	IBookingObjects,
} from '@/shared/types/bookingObjects.types';
import IconHeart from '@/shared/uikit/assets/icons/IconHeart.svg?react';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { SliderButton } from '@/shared/uikit/components/button/sliderButton';
import { Checkbox } from '@/shared/uikit/components/checkbox/Checkbox';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

type PlaceSelectorSheetProps = {
	type: BookingType;
	date: [Date | null, Date | null];
	open: boolean;
	isRadar?: boolean;
	close: () => void;
	onSave: (place: IBookingObjects) => void;
};

const buttonList: IPlaceType[] = [
	{
		label: 'Все',
		type: 'all',
	},
	{
		label: 'Избранные',
		type: 'favourite',
	},
	{
		label: 'Свободные',
		type: 'avaliable',
	},
];

export const PlaceSelectorSheet = ({
	open,
	type,
	date,
	isRadar = false,
	close,
	onSave,
}: PlaceSelectorSheetProps) => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const currentLocation = useSettingsStore((state) => state.currentLocation);
	const bookingLocation = useBookingStore((state) => state.bookingLocation);
	const icon = useIcon({ type: 'lock' });
	const { user } = useContext(AppContext);
	const [places, setPlaces] = useState<IAvaliableObject[]>([]);
	const [filter, setFilter] = useState<IPlaceType['type']>('avaliable');
	// const [favoritesPlaces, setFavoritesPlaces] = useState<IBookingObjects[]>([]);
	const [selectedPlace, setSelectedPlace] = useState<IAvaliableObject | null>(null);

	const { data: bookingObjects, isLoading: isLoadingBookingObjects } = useQuery({
		queryKey: ['availableBookingObjects', currentLocation, bookingLocation, date, type, filter],
		queryFn: () =>
			currentLocation &&
			bookingObjectsApi.getAvailableBookingObjects(
				bookingLocation ? bookingLocation?.id : currentLocation?.id,
				{
					start: date[0] as TDate,
					finish: date[1] as TDate,
					objectKind: BookingObjectKinds[type],
					fetchType: filter,
				},
			),
	});

	const { mutate: addToFavourite, isPending: isAddingFavourite } = useMutation({
		mutationFn: relationApi.addToFavourite,
		onSuccess: () => {
			void useShowNotifaction({ text: 'Добавлено в избранное', type: 'favourite' });
			void queryClient.invalidateQueries({ queryKey: ['availableBookingObjects'] });
		},
		onError: (error: AxiosError<IRequestError>) => {
			void useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const { mutate: removeFromFavourite, isPending: isRemoveFavourite } = useMutation({
		mutationFn: relationApi.removeFromFavourite,
		onSuccess: () => {
			void useShowNotifaction({ text: 'Удаленно из избранных', type: 'favourite' });
			void queryClient.invalidateQueries({ queryKey: ['availableBookingObjects'] });
		},
		onError: (error: AxiosError<IRequestError>) => {
			void useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const handleSearchPlace = (event: ChangeEvent<HTMLInputElement>) => {
		const searchValue = event.currentTarget.value.toLowerCase().trim();

		if (searchValue.length && bookingObjects) {
			const searched = bookingObjects[type]?.objects.filter((place) =>
				place.name
					.replace(/\s\|\s[\d\D]+/gm, '')
					.toLowerCase()
					.match(searchValue),
			);

			if (searched) {
				setPlaces(searched);
			}
			return;
		}
		setPlaces([]);
	};

	const handleSelectPlace = (place: IAvaliableObject) => {
		setSelectedPlace(place);
	};

	const handleAddFavouritePlace = (
		event: MouseEvent<HTMLButtonElement>,
		placeId: IAvaliableObject['id'],
	) => {
		event.stopPropagation();
		addToFavourite({
			booking_object_id: placeId,
			person_id: user?.user.person_id,
			relation: 'favourite',
		});
	};

	const handleRemoveFromFavouritePlace = (
		event: MouseEvent<HTMLButtonElement>,
		placeId: IBookingObjects['id'],
	) => {
		event.stopPropagation();
		removeFromFavourite(placeId);
	};

	const handleClose = () => {
		setSelectedPlace(null);
		setFilter('avaliable');
		close();
	};

	const handleSave = () => {
		if (selectedPlace) {
			onSave(selectedPlace);
			handleClose();
		}
	};

	const content = (
		<Flex direction="column" mt={16}>
			{(places.length ? places : bookingObjects && bookingObjects[type]?.objects)?.map((place) => {
				const relation = place?.relations.find(
					(relation) => relation.booking_object_id === place.id,
				);

				return (
					<LayoutRow
						key={place?.id}
						showDivider
						type="center"
						style={{ padding: 0 }}
						onClick={() =>
							isRadar
								? handleSelectPlace(place)
								: selectedPlace !== place &&
									!place?.is_blocked &&
									place?.type.is_bookable &&
									place?.is_available_for_person &&
									place?.booking_status === 'free' &&
									handleSelectPlace(place)
						}
					>
						<Flex justify="space-between">
							<Flex direction="column">
								<Text size="md" fz={16} fw={600} lts="normal">
									{place?.name.replace(/\s\|\s[\d\D]+/gm, '')}
								</Text>
								<Text c="dimmed" fz={14}>
									{place?.type.description}
								</Text>
							</Flex>
							<Flex align="center" gap={18}>
								<ActionIcon
									variant="transparent"
									onClick={(event) =>
										relation
											? handleRemoveFromFavouritePlace(event, relation.id)
											: handleAddFavouritePlace(event, place.id)
									}
								>
									<IconHeart
										fontSize={24}
										color={relation ? 'rgba(255, 102, 0, 1)' : 'rgba(208, 213, 221, 0.5)'}
									/>
								</ActionIcon>
								{isRadar ? (
									<Checkbox checked={selectedPlace === place} />
								) : place?.is_blocked ||
								  !place?.type.is_bookable ||
								  !place?.is_available_for_person ||
								  place?.booking_status === 'booked' ? (
									icon
								) : (
									<Checkbox checked={selectedPlace === place} />
								)}
							</Flex>
						</Flex>
						{place?.booking_status === 'booked' && (
							<Flex justify="start" align="center" wrap="wrap" columnGap={6} mt={10}>
								{place.bookings.map((book) => (
									<Badge
										size="sm"
										variant="light"
										color="orange"
										key={book.id}
										h="auto"
										py={4}
										styles={{
											label: {
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												lineHeight: 1.2,
											},
										}}
									>
										<span>{dayjs(book.start).format('D MMM')}</span>
										<span>{`${dayjs(book.start).format('HH:mm')} - ${dayjs(book.end).format('HH:mm')}`}</span>
									</Badge>
								))}
							</Flex>
						)}
					</LayoutRow>
				);
			})}
			{!isLoadingBookingObjects && !bookingObjects?.[type] && (
				<Flex direction="column" align="center" justify="center" mt={'35%'}>
					<Text fz={18} fw={700}>
						Пока ничего нет 😔
					</Text>
				</Flex>
			)}
		</Flex>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={open}
				onClose={handleClose}
				trapFocus={false}
				title="Выбор места"
				navbar={
					<Carousel
						withControls={false}
						slidesToScroll="auto"
						slideSize="auto"
						slideGap={8}
						align="start"
					>
						{buttonList?.map((el) => (
							<Carousel.Slide key={el.type}>
								<SliderButton active={filter !== el.type} onClick={() => setFilter(el.type)}>
									{el.label}
								</SliderButton>
							</Carousel.Slide>
						))}
					</Carousel>
				}
				buttons={
					selectedPlace
						? [
								{
									children: 'Выбрать',
									onClick: handleSave,
								},
							]
						: [
								{
									children: 'Выбрать',
									variant: 'light',
								},
							]
				}
				search={{
					onChange: handleSearchPlace,
				}}
				withBackButton={false}
				withCloseButton
				isFullScreen
			>
				{content}
			</BottomSheet>
		);

	return (
		<ModalSheet
			opened={open}
			onClose={handleClose}
			title="Выбор места"
			size="lg"
			buttons={
				selectedPlace
					? [
							{
								children: 'Выбрать',
								onClick: handleSave,
							},
						]
					: [
							{
								children: 'Выбрать',
								variant: 'light',
							},
						]
			}
			centered
		>
			<Box pos="relative">
				<LoadingOverlay
					visible={isLoadingBookingObjects}
					zIndex={1000}
					overlayProps={{ radius: 2, blur: 2 }}
				/>
				<Flex justify="start" direction="column" h={500}>
					<Carousel
						withControls={false}
						slidesToScroll="auto"
						slideSize="auto"
						slideGap={8}
						align="start"
					>
						{buttonList?.map((el) => (
							<Carousel.Slide key={el.type}>
								<SliderButton active={filter !== el.type} onClick={() => setFilter(el.type)}>
									{el.label}
								</SliderButton>
							</Carousel.Slide>
						))}
					</Carousel>

					<Box style={{ overflowY: 'auto' }} h="60dvh" pr="md">
						{content}
					</Box>
				</Flex>
			</Box>
		</ModalSheet>
	);
};
